import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const verifyOwnerPin = async (pin) => {
  // Retry when the server is restarting or busy (no reply / 5xx), so a correct PIN isn't rejected.
  for (let i = 0; ; i += 1) {
    try {
      return await axios.post(`${API}/api/auth/pin`, { pin }, { timeout: 15000 });
    } catch (err) {
      const s = err.response?.status;
      if (i >= 3 || (s && s < 500)) throw err;
      await new Promise((r) => setTimeout(r, 1500 * (i + 1)));
    }
  }
};

export default function PinLock({ children }) {
  const [isUnlocked, setIsUnlocked] = useState(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // The server decides: a valid 24h session cookie unlocks the app.
    axios.get(`${API}/api/auth/status`).then((r) => setIsUnlocked(!!r.data.authenticated)).catch(() => setIsUnlocked(false));
    const expired = () => setIsUnlocked(false);
    window.addEventListener('owner-session-expired', expired);
    return () => window.removeEventListener('owner-session-expired', expired);
  }, []);

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyOwnerPin(pin);
      setIsUnlocked(true);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || (err.response ? `Server error (${err.response.status}) — please try again` : 'Could not reach the server — check your connection and try again'));
      setPin('');
    }
    setBusy(false);
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
    setPin(value);
    setError('');
  };

  if (isUnlocked) {
    return children;
  }
  if (isUnlocked === null) {
    return <div className="min-h-screen bg-gray-900" data-testid="pin-lock-checking" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-6">
      <div className="bg-gray-800/80 backdrop-blur-xl border border-gray-700 rounded-2xl p-8 max-w-sm w-full shadow-2xl">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Clear All Wizard</h1>
          <p className="text-gray-400 text-sm">Enter PIN to access</p>
        </div>

        <form onSubmit={handlePinSubmit} className="space-y-6">
          <div>
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={pin}
              onChange={handlePinChange}
              placeholder="Enter 4-digit PIN"
              className="w-full bg-gray-900/50 border border-gray-600 rounded-xl px-4 py-4 text-center text-2xl text-white tracking-[1em] placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
              autoFocus
              disabled={busy}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm justify-center">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={pin.length !== 4 || busy}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-bold py-4 rounded-xl transition-all"
          >
            Unlock
          </button>
        </form>

        <p className="text-gray-500 text-xs text-center mt-6">
          Authorized access only
        </p>
      </div>
    </div>
  );
}
